import { INFERENCE_ROW_KEY_PREFIX } from "@lobu/core/reserved";
import type {
	ConnectionFacets,
	ConnectorDefinitionItem,
} from "@/lib/api/connections";
import type { InferenceProviderItem } from "@/lib/api/inference-providers";

/**
 * Model providers as connectors-list rows.
 *
 * A provider IS something you connect with a credential that can fail and need
 * rotating — the same operational shape as every other connector row — so it
 * belongs in the connectors list rather than on a bespoke page. But it is NOT a
 * connection: providers live in their own `inference_providers` table, whose
 * DDL CHECK constraints (capabilities-key whitelist, base_url userinfo ban,
 * models_endpoint path whitelist, row-unique api_key_ref, …) are a deliberate
 * security floor that `connections` neither has nor could meaningfully enforce.
 * This is a READ-LAYER union only: we synthesize display rows, we do not move
 * any data.
 *
 * This mirrors the existing "group without a connector definition" synthesis in
 * the connectors index — same pattern, different source table.
 */

/**
 * Namespace prefix for a synthesized provider row's `key`. Provider slugs and
 * connector keys share one map/list, so the prefix keeps a provider named e.g.
 * `slack` from colliding with the real Slack connector. The prefixed key IS the
 * routable connector key: provider rows link to `/connectors/$connectorKey`,
 * where the detail route strips the prefix and resolves the remainder as a
 * provider (or a provider-catalog kind) instead of a connector definition.
 *
 * Defined in `@lobu/core/reserved` because the server's URL builders emit the
 * same prefixed key into chat-message links; re-exported here so browser code
 * keeps one import site for provider-row concerns.
 */
export { INFERENCE_ROW_KEY_PREFIX };

/**
 * The provider slug (or catalog kind) a routable connector key stands for, or
 * null when the key is a plain connector key. The single parse the detail
 * route branches on.
 */
export function parseInferenceRowKey(connectorKey: string): string | null {
	if (!connectorKey.startsWith(INFERENCE_ROW_KEY_PREFIX)) return null;
	const slug = connectorKey.slice(INFERENCE_ROW_KEY_PREFIX.length);
	return slug.length > 0 ? slug : null;
}

/** A connectors-list row synthesized from an `inference_providers` row. */
interface InferenceConnectorRow {
	connector: ConnectorDefinitionItem;
	facets: ConnectionFacets;
	/** The provider this row stands for — drives the row's link target. */
	provider: InferenceProviderItem;
}

/**
 * Turn the org's model providers into connectors-list rows. `installed: true`
 * because an `inference_providers` row only exists once the org added it — the
 * same "it's in your workspace" meaning the list filters on.
 */
export function buildInferenceConnectorRows(
	providers: readonly InferenceProviderItem[],
): InferenceConnectorRow[] {
	return providers.map((provider) => ({
		provider,
		facets: {
			data: false,
			chat: false,
			actions: false,
			audience: false,
			inference: true,
			runtime: false,
		},
		connector: {
			key: `${INFERENCE_ROW_KEY_PREFIX}${provider.slug}`,
			name: provider.displayName || provider.slug,
			description: null,
			version: "",
			auth_schema: null,
			feeds_schema: null,
			actions_schema: null,
			options_schema: null,
			// The provider's own lifecycle state, so the row can show it the way
			// every other connector row shows its state.
			status: provider.status,
			login_enabled: false,
			installed: true,
		} as ConnectorDefinitionItem,
	}));
}
